import React, { useEffect, useRef, useState } from "react";
import { domAnimation, LazyMotion, m, AnimatePresence } from "motion/react";
import { Link, useNavigate } from "../navigation";
import { slidesData } from "../../data/slides";
import { AccentLetter, TexturedHighlight } from "../components/design-system/Typography";
import { AssetIcon, PhotoCredit } from "../components/design-system/MediaPrimitives";
import { Magnet } from "../components/design-system/Magnet";
import { ChronicleNavigationFooter, EditorialHeader } from "../components/design-system/EditorialNavigation";
import { Seo } from "../components/Seo";
import { getChroniclePath, homeSeoPage } from "../../data/seo";
import { seoImageByPageId } from "../../data/seoImages";

const motion = m;

const notePaper2 = new URL("../../assets/papper/Papel2.webp", import.meta.url).href;
const coverTornPaperTexture = new URL("../../assets/grunge/gruge1.webp", import.meta.url).href;
const titlePanelCarton1 = new URL("../../assets/papper/Carton1.webp", import.meta.url).href;
const titlePanelCarton1Mobile = new URL("../../assets/papper/Carton1-movil.webp", import.meta.url).href;
const titlePanelCarton2 = new URL("../../assets/papper/carton2.webp", import.meta.url).href;
const titlePanelCarton3 = new URL("../../assets/papper/carton3.webp", import.meta.url).href;
const titlePanelCarton4 = new URL("../../assets/papper/carton4.webp", import.meta.url).href;
const titlePanelCarton5 = new URL("../../assets/papper/carton5.webp", import.meta.url).href;
const titlePanelCronica1Mobile = new URL("../../assets/papper/Cronica1-movil.webp", import.meta.url).href;
const titlePanelCronica2Mobile = new URL("../../assets/papper/Cronica2-movil.webp", import.meta.url).href;
const titlePanelCronica3Mobile = new URL("../../assets/papper/Cronica3-movil.webp", import.meta.url).href;
const titlePanelCronica4Mobile = new URL("../../assets/papper/Cronica4-movil.webp", import.meta.url).href;
const prisonIcon = new URL("../../assets/iconos/SVG/carcel.svg", import.meta.url).href;
const violenceIcon = new URL("../../assets/iconos/SVG/violencia.svg", import.meta.url).href;
const testimonyIcon = new URL("../../assets/iconos/SVG/testimonio.svg", import.meta.url).href;
const routeIcon = new URL("../../assets/iconos/SVG/ruta.svg", import.meta.url).href;
const lockIcon = new URL("../../assets/iconos/SVG/candado.svg", import.meta.url).href;
const inmateIcon = new URL("../../assets/iconos/SVG/interno.svg", import.meta.url).href;
const routineIcon = new URL("../../assets/iconos/SVG/tiempor-rutina.svg", import.meta.url).href;
const moneyIcon = new URL("../../assets/iconos/SVG/dinero.svg", import.meta.url).href;
const hierarchyIcon = new URL("../../assets/iconos/SVG/gerarquia.svg", import.meta.url).href;
const networkIcon = new URL("../../assets/iconos/SVG/red.svg", import.meta.url).href;
const surveillanceIcon = new URL("../../assets/iconos/SVG/vigilanci.svg", import.meta.url).href;
const advanceIcon = new URL("../../assets/iconos/SVG/avanzar.svg", import.meta.url).href;
const rewindIcon = new URL("../../assets/iconos/SVG/retroceder.svg", import.meta.url).href;

const makeCoverIcons = (icons: string[], slots: string[], rotations: number[]) =>
  icons.map((src, index) => ({
    src,
    className: slots[index],
    rotate: rotations[index],
    delay: 0.9 + index * 0.16,
  }));

const coverIconDecorationsBySlide: Record<string, ReturnType<typeof makeCoverIcons>> = {
  "0": makeCoverIcons(
    [prisonIcon, violenceIcon, testimonyIcon, routeIcon],
    [
      "top-8 right-5 h-10 w-10 md:top-[14%] md:right-[12%]  rotate-15 md:h-28 md:w-28  md:block hidden",
      "top-[32%] right-[22%] h-10 w-24  md:block hidden md:top-[33%] md:right-[7%] md:h-32 md:w-32",
      "top-[73%] left-[6%] h-5 w-20 hidden md:block md:top-[23%] md:left-auto md:right-[30%] md:h-24 md:w-24",
      "bottom-[30%] right-[18%] h-20 w-20 md:bottom-[31%] md:right-[24%] md:h-24 md:w-24  md:block hidden",
    ],
    [-9, 11, -14, 8]
  ),
  "1": makeCoverIcons(
    [lockIcon, inmateIcon, routineIcon, rewindIcon],
    [
      "top-[24%] right-[18%] h-10 w-10 md:top-[18%] md:right-[33%] md:h-24 md:w-24",
      "top-[35%] right-[5%] h-14 w-14 md:top-[43%] md:right-[13%] md:h-32 md:w-32",
      "top-[49%] left-[8%] h-10 w-10 md:bottom-[25%] md:right-[36%] md:left-auto md:top-auto md:h-28 md:w-28",
      "top-[50%] right-[8%] h-10 w-10 md:bottom-[38%] md:right-[25%] md:top-auto md:h-20 md:w-20",
    ],
    [-7, 9, -12, 10]
  ),
  "2": makeCoverIcons(
    [moneyIcon, hierarchyIcon, networkIcon, surveillanceIcon],
    [
      "top-[18%] right-[4%] h-16 w-16 rotate-12 md:top-[24%] md:right-[5%] md:h-24 md:w-24 lg:right-[7%]",
      "top-[31%] right-[31%] h-20 w-20 -rotate-10 md:top-[15%] md:right-[28%] md:h-28 md:w-28 lg:right-[25%]",
      "bottom-[28%] right-[3%] h-16 w-16 rotate-18 md:bottom-[24%] md:right-[9%] md:h-24 md:w-24 lg:right-[13%]",
      "bottom-[39%] right-[48%] h-14 w-14 -rotate-14 md:bottom-[24%] md:right-[38%] md:h-20 md:w-20 lg:right-[42%]",
    ],
    [18, -19, 22, -17]
  ),
  "3": makeCoverIcons(
    [prisonIcon, networkIcon, violenceIcon],
    [
      "top-[20%] right-[8%] h-16 w-16 md:top-[14%] md:right-[9%] md:h-24 md:w-24 lg:right-[12%]",
      "top-[38%] right-[34%] h-20 w-20 md:top-[36%] md:right-[32%] md:h-28 md:w-28 lg:right-[37%]",
      "bottom-[46%] right-[3%] h-14 w-14 md:bottom-[44%] md:right-[8%] md:h-20 md:w-20 lg:right-[14%]",
    ],
    [-11, -8, 13]
  ),
  "4": makeCoverIcons(
    [lockIcon, testimonyIcon, moneyIcon, surveillanceIcon],
    [
      "top-[17%] right-[24%] h-14 w-14 md:top-[15%] md:right-[25%] md:h-[5.5rem] md:w-[5.5rem] lg:h-24 lg:w-24",
      "top-[42%] right-[5%] h-20 w-20 md:top-[30%] md:right-[7%] md:h-28 md:w-28 lg:right-[10%]",
      "bottom-[25%] right-[27%] h-16 w-16 md:bottom-[24%] md:right-[18%] md:h-24 md:w-24 lg:right-[22%]",
      "bottom-[49%] right-[43%] h-14 w-14 md:bottom-[43%] md:right-[38%] md:h-20 md:w-20 lg:right-[44%]",
    ],
    [10, -9, 8, -12]
  ),
  "5": makeCoverIcons(
    [moneyIcon, hierarchyIcon, prisonIcon, routeIcon],
    [
      "top-[18%] right-[7%] h-16 w-16 md:top-[18%] md:right-[8%] md:h-24 md:w-24 lg:right-[12%]",
      "top-[36%] right-[29%] h-[4.5rem] w-[4.5rem] md:top-[32%] md:right-[29%] md:h-28 md:w-28 lg:right-[34%]",
      "bottom-[26%] right-[8%] h-14 w-14 md:bottom-[22%] md:right-[12%] md:h-[5.5rem] md:w-[5.5rem] lg:h-24 lg:w-24",
      "bottom-[45%] right-[45%] h-16 w-16 md:bottom-[41%] md:right-[42%] md:h-20 md:w-20 lg:right-[48%]",
    ],
    [-8, 14, 9, -13]
  ),
};

const coverTestimonyNote: {
  paper: string;
  wrapperClassName: string;
  textBoxClassName: string;
  quoteClassName: string;
  rotate: number;
} = {
  paper: notePaper2,
  wrapperClassName: "absolute bottom-20 rotate-10 -right-10 w-75 aspect-[3/2] min-[341px]:max-[376px]:bottom-[114px] min-[341px]:max-[376px]:right-[-50px] min-[341px]:max-[376px]:w-[260px] max-[341px]:bottom-[118px] max-[341px]:right-[-46px] max-[341px]:w-[255px] sm:right-1 sm:w-60 md:bottom-[185px] md:right-[4.5vw] md:w-[340px] lg:bottom-[90px] lg:w-[440px]",
  textBoxClassName: "absolute left-[19%] right-[19%] text-balance top-[25%] bottom-[24%] z-10 flex flex-col items-center justify-center gap-1.5 overflow-hidden text-center text-black",
  quoteClassName: "uppercase text-[clamp(0.7rem,2.45vw,1.05rem)] md:text-[clamp(1rem,1.28vw,1.25rem)] leading-[1.03] font-black opacity-90 break-words max-w-full max-h-full overflow-hidden",
  rotate: -3,
};

const coverTitlePanelsBySlide: Record<string, {
  paper: string;
  mobilePaper?: string;
  variant: string;
  rotate: number;
  width: number;
  height: number;
  mobileWidth?: number;
  mobileHeight?: number;
}> = {
  "0": {
    paper: titlePanelCarton1,
    mobilePaper: titlePanelCarton1Mobile,
    variant: "is-main",
    rotate: -1.5,
    width: 1920,
    height: 1080,
    mobileWidth: 900,
    mobileHeight: 1600,
  },
  "1": {
    paper: titlePanelCarton2,
    mobilePaper: titlePanelCronica1Mobile,
    variant: "is-cronica-1",
    rotate: 1.8,
    width: 1920,
    height: 1080,
    mobileWidth: 540,
    mobileHeight: 558,
  },
  "2": {
    paper: titlePanelCarton3,
    mobilePaper: titlePanelCronica2Mobile,
    variant: "is-cronica-2",
    rotate: -2.4,
    width: 1920,
    height: 1080,
    mobileWidth: 540,
    mobileHeight: 558,
  },
  "3": {
    paper: titlePanelCarton4,
    mobilePaper: titlePanelCronica3Mobile,
    variant: "is-cronica-3",
    rotate: 2.2,
    width: 1920,
    height: 1080,
    mobileWidth: 540,
    mobileHeight: 558,
  },
  "4": {
    paper: titlePanelCarton5,
    mobilePaper: titlePanelCronica4Mobile,
    variant: "is-cronica-4",
    rotate: -1.8,
    width: 1920,
    height: 1080,
    mobileWidth: 540,
    mobileHeight: 558,
  },
};

const desktopSilhouetteDimensionsBySlide: Record<string, { width: number; height: number }> = {
  "0": { width: 788, height: 1600 },
  "1": { width: 334, height: 594 },
  "2": { width: 740, height: 740 },
  "3": { width: 788, height: 1600 },
  "4": { width: 788, height: 1600 },
  "5": { width: 788, height: 1600 },
};

const mobileSilhouetteDimensionsBySlide: Record<string, { width: number; height: number }> = {
  "0": { width: 203, height: 533 },
  "1": { width: 334, height: 594 },
  "2": { width: 740, height: 740 },
  "3": { width: 203, height: 533 },
  "4": { width: 203, height: 533 },
  "5": { width: 203, height: 533 },
};

const mobileSlideVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1, transition: { duration: 0.28, ease: "easeOut" } },
  exit: { opacity: 0, transition: { duration: 0.18, ease: "easeOut" } },
};

const desktopSlideVariants = {
  enter: (direction: number) => ({ x: direction > 0 ? "14%" : "-14%", opacity: 0, filter: "grayscale(1) contrast(1.35)" }),
  center: { x: "0%", opacity: 1, filter: "grayscale(0) contrast(1)", transition: { duration: 1.15, ease: [0.16, 1, 0.3, 1] } },
  exit: (direction: number) => ({
    x: direction > 0 ? "-12%" : "12%",
    opacity: 0,
    filter: "grayscale(1) contrast(1.45)",
    transition: { duration: 0.62, ease: [0.16, 1, 0.3, 1] },
  }),
};

const mobileBackgroundVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
  exit: { opacity: 0, transition: { duration: 0.16, ease: "easeOut" } },
};

const desktopBackgroundVariants = {
  enter: (direction: number) => ({ x: direction > 0 ? "22%" : "-22%", opacity: 0, scale: 1.04, filter: "grayscale(1) contrast(1.45)" }),
  center: { x: "0%", opacity: 1, scale: 1, filter: "grayscale(0) contrast(1)", transition: { duration: 1.25, ease: [0.16, 1, 0.3, 1] } },
  exit: (direction: number) => ({
    x: direction > 0 ? "-18%" : "18%",
    opacity: 0,
    scale: 1.02,
    filter: "grayscale(1) contrast(1.55)",
    transition: { duration: 0.58, ease: [0.16, 1, 0.3, 1] },
  }),
};

const mobileTextVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1, transition: { duration: 0.22, ease: "easeOut" } },
  exit: { opacity: 0, transition: { duration: 0.14, ease: "easeOut" } },
};

const desktopTextVariants = {
  enter: (direction: number) => ({ x: direction > 0 ? 130 : -130, opacity: 0, filter: "blur(2px) grayscale(1)" }),
  center: { x: 0, opacity: 1, filter: "blur(0px) grayscale(0)", transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1], delay: 0.18 } },
  exit: (direction: number) => ({
    x: direction > 0 ? -90 : 90,
    opacity: 0,
    filter: "blur(1px) grayscale(1)",
    transition: { duration: 0.38, ease: [0.16, 1, 0.3, 1] },
  }),
};

const COVER_TO_CHRONICLE_TRANSITION_MS = 1500;

const headlineHoverVariants = {
  rest: { x: 0, scale: 1 },
  hover: { x: 10, scale: 1.015, transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] } },
};

function renderAccentLetterTest(title: string, slideId: string) {
  if (slideId !== "3" && slideId !== "5") return title;

  const target = "CÁRCEL";
  const targetIndex = title.indexOf(target);
  if (targetIndex === -1) return title;

  return (
    <>
      {title.slice(0, targetIndex)}
      C<AccentLetter label="Á">A</AccentLetter>RCEL
      {title.slice(targetIndex + target.length)}
    </>
  );
}

const highlightHoverVariants = {
  rest: { rotate: -1, scale: 1 },
  hover: { rotate: -2.5, scale: 1.035, transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] } },
};

function HomeContent() {
  const homeShellRef = useRef<HTMLDivElement | null>(null);
  const archiveCutTimeoutRef = useRef<number | null>(null);
  const pointerFrameRef = useRef<number | null>(null);
  const coverExitTimeoutRef = useRef<number | null>(null);
  const latestPointerRef = useRef({ x: 0, y: 0 });
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const [isTabletPortrait, setIsTabletPortrait] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 768 && window.innerWidth <= 1024 && window.innerHeight > window.innerWidth
  );
  const [isHomeCoverColor, setIsHomeCoverColor] = useState(false);
  const [isArchiveCutActive, setIsArchiveCutActive] = useState(false);
  const [homeTouchStart, setHomeTouchStart] = useState<number | null>(null);
  const [homeDragOffset, setHomeDragOffset] = useState(0);
  const [showHomeSwipeHint, setShowHomeSwipeHint] = useState(true);
  const [isCoverExitActive, setIsCoverExitActive] = useState(false);

  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTabletPortrait(window.innerWidth >= 768 && window.innerWidth <= 1024 && window.innerHeight > window.innerWidth);
    };
    window.addEventListener("resize", checkViewport);
    window.addEventListener("orientationchange", checkViewport);
    return () => {
      window.removeEventListener("resize", checkViewport);
      window.removeEventListener("orientationchange", checkViewport);
    };
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => setShowHomeSwipeHint(false), 3600);
    return () => window.clearTimeout(timeout);
  }, []);

  useEffect(() => {
    return () => {
      if (archiveCutTimeoutRef.current) window.clearTimeout(archiveCutTimeoutRef.current);
      if (pointerFrameRef.current) window.cancelAnimationFrame(pointerFrameRef.current);
      if (coverExitTimeoutRef.current) window.clearTimeout(coverExitTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setIsHomeCoverColor(false);
    const timeout = window.setTimeout(() => setIsHomeCoverColor(true), 3000);
    return () => window.clearTimeout(timeout);
  }, [activeSlide]);

  const changeSlide = (index: number, options?: { fromSwipe?: boolean; direction?: number }) => {
    if (index === activeSlide) return;
    setDirection(options?.direction ?? (index > activeSlide ? 1 : -1));
    if (!isMobile) {
      if (archiveCutTimeoutRef.current) window.clearTimeout(archiveCutTimeoutRef.current);
      setIsArchiveCutActive(true);
      archiveCutTimeoutRef.current = window.setTimeout(() => setIsArchiveCutActive(false), 620);
    }
    if (options?.fromSwipe && "vibrate" in navigator) navigator.vibrate(12);
    setActiveSlide(index);
  };

  const slides = slidesData;
  const currentSlide = slides[activeSlide] ?? slides[0];
  const currentChroniclePath = getChroniclePath(currentSlide.id);
  const coverTransitionTitle = slides[0]?.menuTitle ?? "Cárceles, la escuela del crimen";
  const coverIconDecorations = coverIconDecorationsBySlide[currentSlide.id] ?? coverIconDecorationsBySlide["0"];
  const currentTitlePanel = coverTitlePanelsBySlide[currentSlide.id] ?? coverTitlePanelsBySlide["0"];
  const shouldUseCompactTitlePanel = (isMobile || (isTabletPortrait && currentSlide.id === "0")) && currentTitlePanel.mobilePaper;
  const currentTitlePanelPaper = shouldUseCompactTitlePanel ? currentTitlePanel.mobilePaper : currentTitlePanel.paper;
  const currentTitlePanelDimensions =
    shouldUseCompactTitlePanel
      ? { width: currentTitlePanel.mobileWidth ?? currentTitlePanel.width, height: currentTitlePanel.mobileHeight ?? currentTitlePanel.height }
      : { width: currentTitlePanel.width, height: currentTitlePanel.height };
  const desktopSilhouetteDimensions = desktopSilhouetteDimensionsBySlide[currentSlide.id] ?? desktopSilhouetteDimensionsBySlide["0"];
  const mobileSilhouetteDimensions = mobileSilhouetteDimensionsBySlide[currentSlide.id] ?? mobileSilhouetteDimensionsBySlide["0"];
  const isCronica3MobileTitle = isMobile && currentSlide.id === "3";
  const isDesktopMainCover = !isMobile && currentSlide.id === "0";
  const isDesktopCronica2Cover = !isMobile && currentSlide.id === "2";
  const displayFullTitle1 = isCronica3MobileTitle
    ? "¿CÓMO ES FUGARSE"
    : isDesktopCronica2Cover
      ? "LA ESCUELA DEL"
      : currentSlide.fullTitle1;
  const displayFullTitle2 = isCronica3MobileTitle
    ? "DE UNA CÁRCEL"
    : isDesktopMainCover
      ? "LA ESCUELA DEL CRIMEN"
      : isDesktopCronica2Cover
        ? "CRIMEN EN ECUADOR"
        : currentSlide.fullTitle2;
  const displayFullTitle3 = isCronica3MobileTitle
    ? "REGIONAL?"
    : isDesktopMainCover || isDesktopCronica2Cover
      ? ""
      : currentSlide.fullTitle3;
  const isCleanMobileChronicle = isMobile && (currentSlide.id === "1" || currentSlide.id === "2" || currentSlide.id === "3" || currentSlide.id === "4");
  const shouldSkipCoverSilhouette = currentSlide.id === "2" || currentSlide.id === "3" || currentSlide.id === "4" || currentSlide.id === "5";
  const shouldRenderSilhouette = !shouldSkipCoverSilhouette && (!isCleanMobileChronicle || currentSlide.id === "1");
  const hasWalkingSilhouette = currentSlide.id === "1";
  const hasCronica2Silhouette = currentSlide.id === "2";
  const shouldKeepHomeCoverGrayscale = currentSlide.id === "0";
  const shouldShowTestimonyNote = currentSlide.id === "0";
  const shouldHideMobileIcons = isMobile;
  const shouldRenderBackdropSilhouette = shouldRenderSilhouette && currentSlide.id !== "0" && Boolean(currentSlide.silhouetteBackdrop);
  const shouldDockReadButton = currentSlide.id === "1" || currentSlide.id === "4";
  const frontSilhouetteInitial = hasWalkingSilhouette
    ? isMobile
      ? { opacity: 1 }
      : { opacity: 0 }
    : isMobile
      ? { opacity: 0 }
      : { opacity: 0, y: 100 };
  const frontSilhouetteAnimate = hasWalkingSilhouette
    ? isMobile
      ? {
          x: ["88vw", "-132vw"],
          y: 0,
          rotate: [-0.35, 0.35],
        }
      : {
          opacity: [0, 1, 1, 1, 0, 0],
          x: ["58vw", "52vw", "-12vw", "-46vw", "-70vw", "-70vw"],
          y: 0,
          rotate: [-0.8, 0.8],
        }
    : isMobile
      ? { opacity: 1 }
      : { opacity: 1, y: 0 };
  const frontSilhouetteTransition = hasWalkingSilhouette
    ? isMobile
      ? {
          x: { duration: 19, repeat: Infinity, repeatType: "loop", ease: "linear" },
          y: { duration: 0 },
          rotate: { duration: 9.5, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" },
        }
      : {
          opacity: { duration: 19, repeat: Infinity, repeatType: "loop", ease: "linear", times: [0, 0.03, 0.75, 0.8, 0.83, 1] },
          x: { duration: 19, repeat: Infinity, repeatType: "loop", ease: "linear", times: [0, 0.03, 0.58, 0.78, 0.83, 1] },
          y: { duration: 0 },
          rotate: { duration: 9.5, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" },
        }
    : isMobile
      ? { duration: 0.18, ease: "easeOut" }
      : { duration: 1, delay: 0.3, ease: "easeOut" };
  const backdropSilhouetteClassName = hasWalkingSilhouette
    ? "absolute bottom-[-4%] left-[78%] z-0 h-[80%] w-auto max-w-none -translate-x-1/2 object-contain object-bottom opacity-80 min-[341px]:max-[376px]:left-[83%] min-[341px]:max-[376px]:h-[70%] max-[341px]:left-[85%] max-[341px]:h-[68%] sm:left-[76%] sm:h-[90%] md:bottom-[-9%] md:left-[62%] md:h-[112%] lg:bottom-[-11%] lg:left-[64%] lg:h-[122%] xl:h-[128%]"
    : hasCronica2Silhouette
      ? "absolute bottom-[37%] left-[51%] z-0 h-[34%] w-auto max-w-none -translate-x-1/2 object-contain object-bottom opacity-80 min-[341px]:max-[376px]:bottom-[39%] min-[341px]:max-[376px]:left-[54%] min-[341px]:max-[376px]:h-[31%] max-[341px]:bottom-[40%] max-[341px]:left-[56%] max-[341px]:h-[29%] sm:bottom-[36%] sm:left-[52%] sm:h-[40%] md:bottom-[27%] md:left-[43%] md:h-[44%] lg:bottom-[25%] lg:left-[45%] lg:h-[50%] xl:left-[47%] xl:h-[55%] 2xl:h-[58%]"
      : "absolute bottom-0 left-[66%] z-0 h-[88%] w-auto max-w-none -translate-x-1/2 object-contain object-bottom opacity-80 min-[341px]:max-[376px]:left-[77%] min-[341px]:max-[376px]:h-[74%] max-[341px]:left-[79%] max-[341px]:h-[72%] sm:h-[98%] md:left-1/2 md:h-[118%] lg:h-[132%]";
  const backdropSilhouetteMagnetWrapperClassName = hasCronica2Silhouette
    ? "absolute bottom-[37%] left-[51%] z-0 h-[34%] w-fit max-w-none -translate-x-1/2 rotate-[-4deg] min-[341px]:max-[376px]:bottom-[39%] min-[341px]:max-[376px]:left-[54%] min-[341px]:max-[376px]:h-[31%] max-[341px]:bottom-[40%] max-[341px]:left-[56%] max-[341px]:h-[29%] sm:bottom-[36%] sm:left-[52%] sm:h-[40%] md:bottom-[27%] md:left-[43%] md:h-[44%] lg:bottom-[25%] lg:left-[45%] lg:h-[50%] xl:left-[47%] xl:h-[55%] 2xl:h-[58%]"
    : "";
  const mobileFrontSilhouetteWrapperClassName = hasWalkingSilhouette
    ? "absolute right-[18%] bottom-[-1%] z-10 block h-[82%] w-fit drop-shadow-[0_28px_45px_rgba(0,0,0,0.55)] min-[341px]:max-[376px]:right-[18%] min-[341px]:max-[376px]:h-[71%] max-[341px]:right-[14%] max-[341px]:h-[70%] sm:right-[21%] sm:h-[88%] md:relative md:right-auto md:bottom-auto md:block md:hidden"
    : hasCronica2Silhouette
      ? "absolute right-[-12%] bottom-[7%] z-10 block h-[38%] w-fit drop-shadow-[0_28px_45px_rgba(0,0,0,0.55)] min-[341px]:max-[376px]:right-[-14%] min-[341px]:max-[376px]:h-[35%] max-[341px]:right-[-18%] max-[341px]:h-[33%] sm:right-[-8%] sm:h-[44%] md:relative md:right-auto md:bottom-auto md:block md:hidden"
      : "absolute right-0 bottom-0 z-10 block h-[86%] w-fit drop-shadow-[0_28px_45px_rgba(0,0,0,0.55)] min-[341px]:max-[376px]:right-[-42px] min-[341px]:max-[376px]:h-[73%] max-[341px]:right-[-54px] max-[341px]:h-[72%] sm:h-[92%] md:relative md:right-auto md:bottom-auto md:block md:hidden";
  const desktopFrontSilhouetteWrapperClassName = hasWalkingSilhouette
    ? "home-walking-silhouette relative z-10 hidden h-[92%] w-fit mx-auto -translate-x-[14%] drop-shadow-[0_28px_45px_rgba(0,0,0,0.55)] md:block lg:h-[102%] lg:-translate-x-[16%] xl:h-[108%]"
    : hasCronica2Silhouette
      ? "relative z-10 hidden h-[42%] w-fit mx-auto translate-x-[42%] translate-y-[8%] rotate-[9deg] drop-shadow-[0_28px_45px_rgba(0,0,0,0.55)] md:block lg:h-[48%] lg:translate-x-[48%] xl:h-[54%] 2xl:h-[58%]"
      : "relative z-10 hidden h-[96%] w-fit mx-auto drop-shadow-[0_28px_45px_rgba(0,0,0,0.55)] md:block md:translate-y-[8%] lg:h-[106%] lg:translate-y-[10%] xl:h-[112%]";
  const slideVariants = isMobile ? mobileSlideVariants : desktopSlideVariants;
  const backgroundVariants = isMobile ? mobileBackgroundVariants : desktopBackgroundVariants;
  const textVariants = isMobile ? mobileTextVariants : desktopTextVariants;

  const isInteractiveTouchTarget = (target: EventTarget) => {
    const element = target as HTMLElement;
    return Boolean(element.closest?.("a, button, .home-footer, .editorial-menu-overlay"));
  };

  const handleHomeTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || isInteractiveTouchTarget(event.target)) return;
    setHomeTouchStart(event.touches[0].clientX);
    setShowHomeSwipeHint(false);
  };

  const handleHomeTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || homeTouchStart === null) return;
    const distance = event.touches[0].clientX - homeTouchStart;
    setHomeDragOffset(Math.max(-46, Math.min(46, distance)));
  };

  const handleHomeTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || homeTouchStart === null) return;
    const distance = event.changedTouches[0].clientX - homeTouchStart;
    if (Math.abs(distance) > 52) {
      if (distance > 0) {
        changeSlide((activeSlide - 1 + slides.length) % slides.length, { fromSwipe: true, direction: -1 });
      } else {
        changeSlide((activeSlide + 1) % slides.length, { fromSwipe: true, direction: 1 });
      }
    }
    setHomeDragOffset(0);
    setHomeTouchStart(null);
  };

  const setHomePointerVars = (x: number, y: number) => {
    const shell = homeShellRef.current;
    if (!shell) return;
    shell.style.setProperty("--home-pointer-x", x.toFixed(4));
    shell.style.setProperty("--home-pointer-y", y.toFixed(4));
  };

  const scheduleHomePointerUpdate = (x: number, y: number) => {
    latestPointerRef.current = { x, y };
    if (pointerFrameRef.current) return;
    pointerFrameRef.current = window.requestAnimationFrame(() => {
      pointerFrameRef.current = null;
      setHomePointerVars(latestPointerRef.current.x, latestPointerRef.current.y);
    });
  };

  const handleHomeMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isMobile || !homeShellRef.current) return;
    const x = (event.clientX / window.innerWidth - 0.5) * 2;
    const y = (event.clientY / window.innerHeight - 0.5) * 2;
    scheduleHomePointerUpdate(Math.max(-1, Math.min(1, x)), Math.max(-1, Math.min(1, y)));
  };

  const handleHomeMouseLeave = () => {
    scheduleHomePointerUpdate(0, 0);
  };

  const handleReadChronicle = (event: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (isCoverExitActive) return;

    setShowHomeSwipeHint(false);
    setIsArchiveCutActive(true);
    setIsCoverExitActive(true);
    if (coverExitTimeoutRef.current) window.clearTimeout(coverExitTimeoutRef.current);
    coverExitTimeoutRef.current = window.setTimeout(() => {
      navigate(to, { state: { fromHomeCover: true } });
    }, COVER_TO_CHRONICLE_TRANSITION_MS);
  };

  return (
    <>
    <Seo page={homeSeoPage} imageUrl={seoImageByPageId.home} />
    <div
      ref={homeShellRef}
      className="w-full h-screen bg-[#0a0a0a] text-white flex flex-col font-sans overflow-hidden relative"
      data-home-dragging={homeTouchStart !== null ? "true" : "false"}
      data-home-exiting={isCoverExitActive ? "true" : "false"}
      onMouseMove={handleHomeMouseMove}
      onMouseLeave={handleHomeMouseLeave}
      onTouchStart={handleHomeTouchStart}
      onTouchMove={handleHomeTouchMove}
      onTouchEnd={handleHomeTouchEnd}
      onTouchCancel={() => {
        setHomeDragOffset(0);
        setHomeTouchStart(null);
      }}
      style={{
        "--home-drag-offset": `${homeDragOffset}px`,
        "--home-pointer-x": 0,
        "--home-pointer-y": 0,
      } as React.CSSProperties}
    >
      <section className="sr-only" aria-label="Contenido del especial">
        <h1>{slides[0].menuTitle}</h1>
        <p>{slides[0].desc}</p>
        {slides.slice(1).map((slide) => (
          <article key={`home-outline-${slide.id}`}>
            <h2>{slide.menuTitle}</h2>
            <p>{slide.desc}</p>
          </article>
        ))}
      </section>
      
      {/* AREA DE SLIDER (CAPAS ANIMADAS) */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence custom={direction} initial={false}>
          <motion.div
            key={activeSlide}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="absolute inset-0 flex items-center justify-center overflow-visible"
          >
            {/* CAPA 0: FONDO */}
            <div className="home-background-parallax absolute inset-0 z-0 group">
              <motion.img
                variants={backgroundVariants}
                custom={direction}
                src={currentSlide.img}
                alt="Fondo"
                className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                  shouldKeepHomeCoverGrayscale
                    ? "grayscale opacity-40 mix-blend-luminosity"
                    : isHomeCoverColor
                      ? "grayscale-0 opacity-55 mix-blend-normal"
                      : "grayscale opacity-40 mix-blend-luminosity group-hover:grayscale-0 group-hover:opacity-55 group-hover:mix-blend-normal"
                }`}
              />
              <div className="home-cover-grunge absolute inset-0 opacity-30 mix-blend-overlay" />
              {currentSlide.type === "main" && (
                <img
                  src={coverTornPaperTexture}
                  alt=""
                  aria-hidden="true"
                  width={1200}
                  height={800}
                  fetchPriority={currentSlide.id === "0" ? "high" : "auto"}
                  className="home-cover-torn-paper absolute inset-y-0 right-0 w-[62%] opacity-[0.18] mix-blend-screen"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-90" />
            </div>

            {/* CAPA 2: PERSONA / SILUETA */}
            <div className="home-silhouette-stage" data-slide={currentSlide.id}>
              {shouldRenderBackdropSilhouette && hasCronica2Silhouette ? (
                <Magnet
                  disabled={isMobile}
                  padding={190}
                  magnetStrength={18}
                  wrapperClassName={backdropSilhouetteMagnetWrapperClassName}
                  innerClassName="h-full w-fit"
                >
                  <motion.img
                    initial={isMobile ? { opacity: 0 } : { opacity: 0, y: 120, scale: 0.94 }}
                    animate={isMobile ? { opacity: 0.72 } : { opacity: 0.86, y: 0, scale: 1 }}
                    transition={isMobile ? { duration: 0.18, ease: "easeOut" } : { duration: 1, delay: 0.18, ease: "easeOut" }}
                    src={currentSlide.silhouetteBackdrop}
                    alt=""
                    aria-hidden="true"
                    className="block h-full w-auto max-w-none object-contain object-bottom"
                  />
                </Magnet>
              ) : shouldRenderBackdropSilhouette ? (
                <motion.img
                  initial={isMobile ? { opacity: 0 } : { opacity: 0, y: 120, scale: 0.94 }}
                  animate={isMobile ? { opacity: hasWalkingSilhouette ? 1 : 0.72 } : { opacity: hasWalkingSilhouette ? 1 : 0.86, y: 0, scale: 1 }}
                  transition={isMobile ? { duration: 0.18, ease: "easeOut" } : { duration: 1, delay: 0.18, ease: "easeOut" }}
                  src={currentSlide.silhouetteBackdrop}
                  alt=""
                  aria-hidden="true"
                  className={backdropSilhouetteClassName}
                />
              ) : null}
              {shouldRenderSilhouette && currentSlide.silhouetteMobile ? (
                <Magnet
                  disabled={isMobile}
                  padding={hasCronica2Silhouette ? (isMobile ? 170 : 190) : (isMobile ? 90 : 110)}
                  magnetStrength={hasCronica2Silhouette ? (isMobile ? 16 : 18) : (isMobile ? 38 : 45)}
                  wrapperClassName={isMobile ? mobileFrontSilhouetteWrapperClassName : desktopFrontSilhouetteWrapperClassName}
                  innerClassName="h-full w-fit"
                >
                  <motion.img
                    initial={frontSilhouetteInitial}
                    animate={frontSilhouetteAnimate}
                    transition={frontSilhouetteTransition}
                    src={isMobile ? currentSlide.silhouetteMobile : currentSlide.silhouette}
                    alt="Silueta"
                    width={isMobile ? mobileSilhouetteDimensions.width : desktopSilhouetteDimensions.width}
                    height={isMobile ? mobileSilhouetteDimensions.height : desktopSilhouetteDimensions.height}
                    fetchPriority={currentSlide.id === "0" ? "high" : "auto"}
                    className={`block h-full w-auto max-w-none object-contain object-bottom ${isMobile ? "" : "mx-auto"}`}
                  />
                </Magnet>
              ) : shouldRenderSilhouette ? (
                <Magnet
                  disabled={isMobile}
                  padding={100}
                  magnetStrength={42}
                  wrapperClassName="home-generic-silhouette"
                  innerClassName="h-full w-fit"
                >
                  <motion.img
                    initial={frontSilhouetteInitial}
                    animate={frontSilhouetteAnimate}
                  transition={frontSilhouetteTransition}
                  src={currentSlide.silhouette}
                  alt="Silueta"
                  width={desktopSilhouetteDimensions.width}
                  height={desktopSilhouetteDimensions.height}
                  fetchPriority={currentSlide.id === "0" ? "high" : "auto"}
                  className="block h-full w-auto max-w-none object-contain object-bottom"
                />
                </Magnet>
              ) : null}
            </div>

            {/* CAPA 1: TIPOGRAFÍA */}
            <div className="home-title-panel-stage absolute inset-0 z-[5] pointer-events-none overflow-visible md:mx-10">
              <motion.img
                key={`title-panel-${currentSlide.id}`}
                initial={isMobile ? { opacity: 0 } : { opacity: 0, scale: 0.94 }}
                animate={isMobile ? { opacity: 1 } : { opacity: 1, scale: 1 }}
                transition={isMobile ? { duration: 0.16, ease: "easeOut" } : { duration: 0.7, delay: 0.12, ease: "easeOut" }}
                src={currentTitlePanelPaper}
                alt=""
                aria-hidden="true"
                width={currentTitlePanelDimensions.width}
                height={currentTitlePanelDimensions.height}
                loading="eager"
                fetchPriority={currentSlide.id === "0" ? "high" : "auto"}
                className={`home-title-panel ${currentTitlePanel.variant}`}
              />
            </div>

            <div
              className={`home-copy-area ${isCleanMobileChronicle ? "is-clean-mobile" : ""}`}
              data-slide={currentSlide.id}
            >
              <motion.div
                variants={textVariants}
                custom={direction}
                className={`home-copy-column ${isCleanMobileChronicle ? "is-clean-mobile" : ""}`}
              >
                <motion.div
                  className={`home-headline-group ${isCleanMobileChronicle ? "is-clean-mobile" : ""}`}
                  initial="rest"
                  animate="rest"
                  whileHover={isMobile ? undefined : "hover"}
                >
                  <span className="home-chronicle-badge">
                    {currentSlide.type === "main" ? "Especial" : currentSlide.title}
                  </span>

                  <motion.div
                    variants={headlineHoverVariants}
                    className="home-title-line home-title-primary"
                    aria-hidden="true"
                  >
                    <span className="block drop-shadow-[0_0_20px_rgba(0,0,0,0.9)] transition-colors duration-300">
                      {displayFullTitle1}
                    </span>
                  </motion.div>

                  {displayFullTitle2 && (
                    <motion.div
                      initial={isMobile ? { opacity: 1 } : { rotate: -2, scale: 0.9 }}
                      animate={isMobile ? { opacity: 1 } : { rotate: -1, scale: 1 }}
                      variants={highlightHoverVariants}
                      transition={isMobile ? { duration: 0 } : { delay: 0.3, duration: 0.5, type: "spring" }}
                      className="relative z-10 my-2 lg:my-4 inline-block drop-shadow-[0_0_25px_rgba(0,0,0,1)] max-[376px]:my-1"
                    >
                      <div className="m-0" aria-hidden="true">
                        <TexturedHighlight
                          texture={currentSlide.type === "main" ? 2 : 1}
                          position={currentSlide.type === "main" ? "68% 46%" : "24% 58%"}
                          blendMode="multiply"
                          className="home-title-line home-title-highlight"
                        >
                        {renderAccentLetterTest(displayFullTitle2, currentSlide.id)}
                        </TexturedHighlight>
                      </div>
                    </motion.div>
                  )}

                  {displayFullTitle3 && (
                    <motion.div
                      variants={headlineHoverVariants}
                      className="home-title-line home-title-third"
                      aria-hidden="true"
                    >
                      <span className="block drop-shadow-[0_0_20px_rgba(0,0,0,0.9)] transition-colors duration-300">
                        {displayFullTitle3}
                      </span>
                    </motion.div>
                  )}
                </motion.div>

                <motion.div
                  initial={isMobile ? { opacity: 0 } : { opacity: 0, y: 20 }}
                  animate={isMobile ? { opacity: 1 } : { opacity: 1, y: 0 }}
                  transition={isMobile ? { duration: 0.16, ease: "easeOut" } : { delay: 0.6, duration: 0.8 }}
                  className={`home-copy ${isCleanMobileChronicle ? "is-clean-mobile" : ""}`}
                >
                  <p className="home-description">
                    {currentSlide.desc}
                  </p>
                  
                  {currentSlide.type === "main" ? (
                    <div className="home-author-row">
                      <span className="h-[2px] w-6 md:w-8 bg-[#ffd200] drop-shadow-md min-[341px]:max-[376px]:w-5 max-[341px]:w-5"></span>
                      <p className="home-author">
                        POR: {currentSlide.author}
                      </p>
                    </div>
                  ) : (
                    <Link
                      to={currentChroniclePath}
                      onClick={(event) => handleReadChronicle(event, currentChroniclePath)}
                      className={`home-inline-cta group ${shouldDockReadButton ? "is-docked" : ""} ${isCleanMobileChronicle ? "is-clean-mobile" : ""}`}
                    >
                      LEER CRÓNICA
                      <AssetIcon src={advanceIcon} className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-2" />
                    </Link>
                  )}
                </motion.div>
              </motion.div>
            </div>

            {/* CAPA 3: ICONOS Y PAPEL DE TESTIMONIO */}
            <div className="home-icon-stage absolute inset-0 z-30 pointer-events-none">
                {!shouldHideMobileIcons && coverIconDecorations.map((icon) => (
                  <Magnet
                    key={icon.src}
                    disabled={isMobile}
                    padding={currentSlide.id === "2" ? 130 : 50}
                    magnetStrength={currentSlide.id === "2" ? 18 : 50}
                    wrapperClassName={`absolute ${icon.className}`}
                    innerClassName="h-full w-full"
                  >
                    <motion.div
                      initial={isMobile ? { opacity: 0 } : { opacity: 0, scale: 0.35, rotate: icon.rotate - 18 }}
                      animate={isMobile ? { opacity: 0.72 } : { opacity: 0.86, scale: 1, rotate: icon.rotate }}
                      transition={isMobile ? { duration: 0.2, delay: 0.05, ease: "easeOut" } : { duration: 0.65, delay: icon.delay, type: "spring", stiffness: 180, damping: 13 }}
                      className="h-full w-full"
                    >
                      <AssetIcon src={icon.src} className="home-cover-icon" />
                    </motion.div>
                  </Magnet>
                ))}
                {shouldShowTestimonyNote && (
                <motion.div
                  key={`testimony-note-${currentSlide.id}`}
                  initial={isMobile ? { opacity: 0 } : { opacity: 0, scale: 0.8, rotate: coverTestimonyNote.rotate - 4, y: -20 }}
                  animate={isMobile ? { opacity: 1 } : { opacity: 1, scale: 1, rotate: coverTestimonyNote.rotate, y: 0 }}
                  transition={isMobile ? { duration: 0.16, ease: "easeOut" } : { duration: 0.6, delay: 0.85, type: "spring" }}
                  className={coverTestimonyNote.wrapperClassName}
                >
                  <div className="absolute inset-0 z-0">
                    <img src={coverTestimonyNote.paper} alt="Papel rasgado del testimonio" width={900} height={600} className="w-full h-full object-contain pointer-events-none" />
                  </div>
                  <div className={coverTestimonyNote.textBoxClassName}>
                    <p className={coverTestimonyNote.quoteClassName}>
                      "La cárcel no me rehabilitó. Me enseñó. Me conectó. Me cambió"
                    </p>
                  </div>
                </motion.div>
                )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {!isMobile && isArchiveCutActive && (
          <motion.div
            className="home-archive-cut"
            initial={{ opacity: 0, x: direction > 0 ? 28 : -28 }}
            animate={{ opacity: [0, 0.9, 0.25], x: 0 }}
            exit={{ opacity: 0, x: direction > 0 ? -24 : 24 }}
            transition={{ duration: 0.46, ease: [0.16, 1, 0.3, 1] }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isCoverExitActive && (
          <motion.div
            className="home-transition-title-overlay"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
          >
            <motion.div
              className="home-transition-title"
              initial={{ opacity: 0, y: 28, scale: 0.96, filter: "blur(8px)" }}
              animate={{
                opacity: [0, 1, 1],
                y: [28, 0, 0],
                scale: [0.96, 1, 1],
                filter: ["blur(8px)", "blur(0px)", "blur(0px)"],
              }}
              transition={{ duration: 1.24, times: [0, 0.34, 1], ease: [0.16, 1, 0.3, 1] }}
            >
              {coverTransitionTitle}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- CAPA 4: INTERFAZ --- */}
      <div className="home-interface-layer">
        <EditorialHeader
          badge={currentSlide.type === "main" ? "Especial" : currentSlide.title}
          onHomeClick={() => changeSlide(0)}
        />

        <div className={`home-hero-photo-credit ${currentSlide.id === "0" || currentSlide.id === "1" ? "is-vertical" : ""}`}>
          <PhotoCredit credit={currentSlide.imgCredit} className="home-hero-photo-credit-text" />
        </div>

        {shouldDockReadButton && (
          <Link
            to={currentChroniclePath}
            onClick={(event) => handleReadChronicle(event, currentChroniclePath)}
            className="home-read-cta read-cta-pulse group"
          >
            LEER CRÓNICA
            <AssetIcon src={advanceIcon} className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-2" />
          </Link>
        )}

        {showHomeSwipeHint && (
          <motion.div
            className="home-mobile-swipe-hint"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            <span aria-hidden="true">←</span>
            <b aria-hidden="true" />
            <span aria-hidden="true">→</span>
          </motion.div>
        )}
      </div>

      <div className="home-footer-layer">
        <ChronicleNavigationFooter
          activeId={currentSlide.id}
          onSelect={changeSlide}
          showMobileCredits
          autoScrollOnSelect={false}
        />
      </div>

      <div className="home-ui-grain" aria-hidden="true" />
    </div>
    </>
  );
}

export function Home() {
  return (
    <LazyMotion features={domAnimation}>
      <HomeContent />
    </LazyMotion>
  );
}
